import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Planet } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1644325349124-d1756b79dd42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMHNjaWVudGlmaWMlMjBkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3ODYxNDkwMDV8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0E14]">
      <div className="hidden lg:block relative overflow-hidden border-r border-white/5">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">Scientific Observatory</p>
          <h2 className="text-4xl font-bold tracking-tight text-white leading-tight">Editorial intelligence,<br/>curated with rigor.</h2>
          <p className="text-slate-400 mt-4 max-w-md text-sm leading-relaxed">Collect, annotate, and adjudicate research signals through a complete editorial workflow.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex items-center gap-2 mb-8">
            <Planet size={28} weight="duotone" className="text-blue-500" />
            <span className="text-lg font-semibold tracking-tight">Observatory</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight mb-1">Sign in</h1>
          <p className="text-slate-400 text-sm mb-6">Access the editorial workspace.</p>
          {error && <div data-testid="login-error" className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">{error}</div>}
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-[#131722] border-white/10 focus:ring-2 focus:ring-blue-500" placeholder="you@observatory.org" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-[#131722] border-white/10 focus:ring-2 focus:ring-blue-500" placeholder="••••••••" />
            </div>
          </div>
          <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full mt-6 bg-blue-600 hover:bg-blue-500 rounded-full font-medium transition-colors duration-200">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-center text-xs text-slate-500 mt-6">Accounts are created by an administrator. Contact your admin for access.</p>
        </form>
      </div>
    </div>
  );
}
